import { Link } from 'react-router-dom'
import { TremorLogo } from '@/shared/components/ui/TremorLogo'
import { ArrowLeft } from 'lucide-react'

interface LegalPageProps {
  title: string
  subtitle: string
  children: React.ReactNode
}

export function LegalPage({ title, subtitle, children }: LegalPageProps) {
  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <div className="bg-onyx">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Link to="/home" className="inline-flex items-center gap-2 text-paper/50 hover:text-paper transition-colors text-sm mb-6">
            <ArrowLeft size={14} /> Retour à TremorApp
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <TremorLogo size={28} className="text-gold" />
            <p className="font-serif text-xl font-bold text-paper tracking-tight">
              tremor<em className="not-italic font-light text-gold">app</em>
            </p>
          </div>
          <h1 className="font-serif text-3xl font-bold text-paper tracking-tight">{title}</h1>
          <p className="text-sm text-paper/50 mt-2">{subtitle}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="prose-legal">
          {children}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-ink-border/30 bg-paper-warm">
        <div className="max-w-4xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <TremorLogo size={14} className="text-gold/40" />
            <span className="text-[10px] text-ink-faint tracking-wider uppercase">TremorApp · Intelligence Marché · SaaS B2B</span>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-ink-faint">
            <Link to="/cgu" className="hover:text-ink transition-colors">CGU</Link>
            <span>·</span>
            <Link to="/cgv" className="hover:text-ink transition-colors">CGV</Link>
            <span>·</span>
            <a href="mailto:legal@tremor.fr" className="hover:text-ink transition-colors">legal@tremor.fr</a>
          </div>
        </div>
      </div>

      <style>{`
        .prose-legal h2 {
          font-family: 'Fraunces', Georgia, serif;
          font-size: 1.25rem;
          font-weight: 700;
          color: #232018;
          margin-top: 2.5rem;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #E8E5DE;
        }
        .prose-legal h3 {
          font-size: 0.95rem;
          font-weight: 600;
          color: #232018;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .prose-legal p {
          font-size: 0.875rem;
          line-height: 1.75;
          color: #4A4539;
          margin-bottom: 0.75rem;
        }
        .prose-legal ul {
          list-style: none;
          padding-left: 0;
          margin-bottom: 1rem;
        }
        .prose-legal ul li {
          font-size: 0.875rem;
          line-height: 1.75;
          color: #4A4539;
          padding-left: 1.25rem;
          position: relative;
          margin-bottom: 0.25rem;
        }
        .prose-legal ul li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.6rem;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #C9A84C;
        }
        .prose-legal table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          font-size: 0.8rem;
        }
        .prose-legal thead th {
          background: #232018;
          color: #F5F3EF;
          padding: 0.6rem 0.75rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .prose-legal tbody td {
          padding: 0.5rem 0.75rem;
          border-bottom: 1px solid #E8E5DE;
          color: #4A4539;
          vertical-align: top;
        }
        .prose-legal tbody tr:nth-child(even) {
          background: #FAF9F6;
        }
        .prose-legal .note {
          background: #FEF9E8;
          border-left: 3px solid #C9A84C;
          padding: 0.75rem 1rem;
          margin: 1rem 0;
          font-size: 0.8rem;
          font-style: italic;
          color: #6B6050;
          border-radius: 0 8px 8px 0;
        }
        .prose-legal .contact-table td:first-child {
          font-weight: 600;
          color: #232018;
          white-space: nowrap;
        }
        .prose-legal .contact-table a {
          color: #C9A84C;
          text-decoration: none;
        }
        .prose-legal .contact-table a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  )
}
